import * as z from "zod";
import { streamPlatforms } from "../stream-platform";

// Array order controls prompt columns; history priority keeps compound edits specific.
export const slotFields = [
  {
    key: "active",
    schema: z.boolean(),
    initial: false,
    column: "active",
    historyKey: "history.slotActive",
    priority: 6,
  },
  {
    key: "platform",
    schema: z
      .enum(streamPlatforms)
      .describe(
        "Use 'YT' for explicit YouTube members-only streams, 'Twitch' for explicit Twitch streams, and 'both' only when both are confirmed. Otherwise, use 'none'. Never infer platforms from context or prior knowledge.",
      ),
    initial: "none",
    column: "platform",
    historyKey: "history.slotPlatform",
    priority: 5,
  },
  {
    key: "memberOnly",
    schema: z.boolean().optional().describe("YouTube members-only stream."),
    initial: undefined,
    column: "memberOnly",
    historyKey: "history.slotMemberOnly",
    priority: 4,
  },
  {
    key: "time",
    schema: z.union([z.literal(""), z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/)]),
    initial: "19:00",
    column: "start",
    historyKey: "history.slotTime",
    priority: 2,
  },
  {
    key: "duration",
    schema: z.number().min(30).max(360).multipleOf(30),
    initial: 120,
    column: "minutes",
    historyKey: "history.slotDuration",
    priority: 3,
  },
  { key: "title", schema: z.string(), initial: "", column: "title", historyKey: "history.slotTitle", priority: 0 },
  {
    key: "subheading",
    schema: z.string(),
    initial: "",
    column: "subheading",
    historyKey: "history.slotSubheading",
    priority: 1,
  },
] as const;

type SlotField = (typeof slotFields)[number];
type SlotShape = { [Field in SlotField as Field["key"]]: Field["schema"] };
const slotShape = Object.fromEntries(slotFields.map((field) => [field.key, field.schema])) as SlotShape;

export const SlotSchema = z.object(slotShape);
export type StreamSlot = z.infer<typeof SlotSchema>;

const initialFields = slotFields.filter((field) => field.initial !== undefined);
export const emptySlot = Object.fromEntries(initialFields.map((field) => [field.key, field.initial])) as StreamSlot;

export const slotHistoryFields = [...slotFields].sort((left, right) => left.priority - right.priority);

export function slotPromptValues(slot: StreamSlot) {
  return slotFields.map(({ key, schema }) => {
    if (key === "active") return slot.active ? "on" : "off";
    const value = slot[key];
    if (value === undefined) {
      const optionalBoolean = schema instanceof z.ZodOptional && schema.unwrap() instanceof z.ZodBoolean;
      return optionalBoolean ? "false" : "";
    }
    return String(value);
  });
}
